import React, { JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  DocumentDuplicateIcon,
  MegaphoneIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { useAuth } from 'context/user'
import { useEntitlements } from 'context/plan'
import { AccountOverlayLinkState, useAccountOverlayLink } from 'hooks/useAccountOverlayLink'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import { SavedNote } from 'components/SavedNote'
import { TICKER_BAR_MARQUEE_WIDTH, TickerBarMarquee, TickerBarMarqueeInput } from 'components/TickerBarMarquee'
import { graphqlRequest } from 'lib/helpers'
import { ensureFontLoaded } from 'lib/fontLoader'
import { sortByName } from 'lib/elementOrder'
import {
  MAX_TICKER_BAR_NAME,
  MAX_TICKER_BARS_PER_STREAM,
  TICKER_BAR_COPY,
  TickerBarDraft,
  TickerBarDraftProblem,
  TickerBarMessageDraft,
  clipToUnits,
  newTickerBarDraft,
  normalizedTickerBarMessagesOf,
  tickerBarDraftOf,
  tickerBarDraftProblems,
  tickerBarNearBudget,
  tickerBarSaveInputOf
} from 'lib/tickerBarConfig'
import { DeletionResult, EventDetail, TickerBar, TickerBarMessage, TickerBarSummary } from 'types/api'
import {
  FieldLabel,
  OVERLAY_LINK_NOTE,
  OverlayLinkBlock,
  OverlayLinkRowActions,
  QuickOverlayManagerView,
  SectionCard,
  STREAM_ENDED_NOTE,
  StreamScope,
  reapplyStreamWrite
} from 'components/chatOverlays/ChatOverlayManager'
import { CopyIntoStreamDialog, updatedLabelOf } from 'components/chatOverlays/CopyIntoStreamDialog'
import { TickerBarSharedCard, TickerBarStyleCard, tickerBarInputClass } from 'components/chatOverlays/TickerBarStyleCard'
import listMyTickerBarsQuery from 'graphql/listMyTickerBars.graphql'
import getTickerBarQuery from 'graphql/getTickerBar.graphql'
import createTickerBarMutation from 'graphql/createTickerBar.graphql'
import updateTickerBarMutation from 'graphql/updateTickerBar.graphql'
import deleteTickerBarMutation from 'graphql/deleteTickerBar.graphql'
import duplicateTickerBarMutation from 'graphql/duplicateTickerBar.graphql'
import createEventTickerBarMutation from 'graphql/createEventTickerBar.graphql'
import updateEventTickerBarMutation from 'graphql/updateEventTickerBar.graphql'
import deleteEventTickerBarMutation from 'graphql/deleteEventTickerBar.graphql'

export const TICKER_BAR_MANAGER_COPY = Object.freeze({
  heading: 'Message tickers',
  newButton: 'New Message ticker',
  empty: TICKER_BAR_COPY.emptyList,
  createFirst: 'Create the first one',
  createTitle: 'New Message ticker',
  editTitle: 'Edit Message ticker',
  nameHelp: 'Shown in your Message tickers list only, never on the overlay.',
  namePlaceholder: 'News ticker, Sponsor ticker, …',
  createButton: 'Create Message ticker',
  saveButton: 'Save changes',
  showNowButton: 'Show changes now',
  cancel: 'Cancel',
  preview: 'Preview',
  linkHeading: 'Browser source URL',
  linkAfterSave: 'The browser-source link appears here (and in the list) after the first save.',
  listFooter: 'Each Message ticker has its own short browser-source link: add it to OBS with a transparent background. Links do not expire. Regenerate gives that ticker a new link and turns the old one off everywhere.',
  foreignNote: 'This Message ticker belongs to another account. Its link and Duplicate are for its owner only.',
  duplicate: 'Duplicate',
  gone: 'That Message ticker no longer exists.',
  problemsLead: 'Fix these before saving:',
  messageProblems: 'Some messages above need a fix (marked in red).',
  deleteConfirm: (name: string): string => `Delete Message ticker "${name}"? Any browser source still showing its link goes blank.`,
  capReached: (cap: number): string => `Your account has reached its Message ticker limit (${cap}).`
})

export const STREAM_TICKER_BAR_COPY = Object.freeze({
  heading: 'Message tickers in this stream',
  newButton: 'New Message ticker',
  copyButton: 'Add one I already made',
  empty: 'No Message tickers in this stream yet.',
  note: 'Message tickers made here belong to this stream. They show through this stream\'s key when you place them in a scene, and they are deleted with the stream. For a Message ticker with its own link, use Quick Overlays.',
  sideNote: 'Place this Message ticker in a scene in the overlay editor. It shows through this stream\'s key, so there is no separate link to copy.',
  atMost: `A stream can hold at most ${MAX_TICKER_BARS_PER_STREAM} Message tickers`,
  deleteConfirm: (name: string): string => `Delete Message ticker "${name}"? It is removed from every scene in this stream.`
})

export const streamTickerBarRowsOf = (event: Pick<EventDetail, 'tickerBars'>): TickerBar[] =>
  sortByName(event.tickerBars ?? [], (t) => t.name, (t) => t.tickerBarId)

export const withStreamTickerBarRow = (event: EventDetail, row: TickerBar): EventDetail => {
  const rows = event.tickerBars ?? []
  const exists = rows.some((t) => t.tickerBarId === row.tickerBarId)
  return { ...event, tickerBars: exists ? rows.map((t) => (t.tickerBarId === row.tickerBarId ? row : t)) : [...rows, row] }
}

export const withoutStreamTickerBarRow = (event: EventDetail, tickerBarId: string): EventDetail =>
  ({ ...event, tickerBars: (event.tickerBars ?? []).filter((t) => t.tickerBarId !== tickerBarId) })

export const tickerBarSummaryOf = (row: TickerBar): TickerBarSummary =>
  ({ tickerBarId: row.tickerBarId, name: row.name, createdAt: row.createdAt, updatedAt: row.updatedAt })

export const isForeignTickerBar = (row: Pick<TickerBar, 'accountId' | 'eventId'> | null, callerAccountId: string | null): boolean =>
  row != null && row.eventId == null && callerAccountId != null && row.accountId !== callerAccountId

export const tickerBarSaveRequestOf = ({ draft, existing, eventId, restartNow }: {
  draft: TickerBarDraft
  existing: Pick<TickerBar, 'tickerBarId'> | null
  eventId: string | null
  restartNow: boolean
}): { doc: string, variables: Record<string, unknown> } => {
  const input = tickerBarSaveInputOf(draft)
  if (existing == null) {
    return eventId == null
      ? { doc: createTickerBarMutation, variables: { ...input } }
      : { doc: createEventTickerBarMutation, variables: { eventId, ...input } }
  }
  const update = { tickerBarId: existing.tickerBarId, ...input, restartNow }
  return eventId == null
    ? { doc: updateTickerBarMutation, variables: update }
    : { doc: updateEventTickerBarMutation, variables: { eventId, ...update } }
}

export const tickerBarSummaryProblemLines = (problems: TickerBarDraftProblem[]): string[] => {
  const lines: string[] = []
  let perMessage = false
  for (const p of problems) {
    if (p.index != null) {
      perMessage = true
    } else if (p.place === 'top' || p.place === 'bottom') {
      lines.push(`${p.place === 'top' ? 'Top bar' : 'Bottom bar'}: ${p.message}`)
    } else {
      lines.push(p.message)
    }
  }
  if (perMessage) lines.push(TICKER_BAR_MANAGER_COPY.messageProblems)
  return lines
}

export const TICKER_BAR_PREVIEW_RESTART_MS = 400

export interface PreviewRestartDebouncer<T> {
  push: (value: T) => void
  dispose: () => void
}

export const createPreviewRestartDebouncer = <T,>(
  onFire: (value: T) => void,
  delayMs: number = TICKER_BAR_PREVIEW_RESTART_MS
): PreviewRestartDebouncer<T> => {
  let timer: ReturnType<typeof globalThis.setTimeout> | null = null
  return {
    push: (value) => {
      if (timer != null) globalThis.clearTimeout(timer)
      timer = globalThis.setTimeout(() => {
        timer = null
        onFire(value)
      }, delayMs)
    },
    dispose: () => {
      if (timer != null) globalThis.clearTimeout(timer)
      timer = null
    }
  }
}

interface ShownMessages {
  top: TickerBarMessage[]
  bottom: TickerBarMessage[]
  restartKey: number
}

const NO_MESSAGES: TickerBarMessage[] = []

const usePreviewMessages = (top: TickerBarMessageDraft[], bottom: TickerBarMessageDraft[]): ShownMessages => {
  const [shown, setShown] = useState<ShownMessages>(() => ({
    top: normalizedTickerBarMessagesOf(top, 't'),
    bottom: normalizedTickerBarMessagesOf(bottom, 'b'),
    restartKey: 0
  }))
  const debouncerRef = useRef<PreviewRestartDebouncer<{ top: TickerBarMessage[], bottom: TickerBarMessage[] }> | null>(null)
  if (debouncerRef.current == null) {
    debouncerRef.current = createPreviewRestartDebouncer((next) => {
      setShown((prev) => ({ top: next.top, bottom: next.bottom, restartKey: prev.restartKey + 1 }))
    })
  }
  const firstRef = useRef(true)
  useEffect(() => {
    if (firstRef.current) {
      firstRef.current = false
      return
    }
    debouncerRef.current?.push({
      top: normalizedTickerBarMessagesOf(top, 't'),
      bottom: normalizedTickerBarMessagesOf(bottom, 'b')
    })
  }, [top, bottom])
  useEffect(() => () => debouncerRef.current?.dispose(), [])
  return shown
}

const usePreviewWidth = (): { width: number, hostRef: (node: HTMLDivElement | null) => (() => void) | undefined } => {
  const [width, setWidth] = useState(960)
  const hostRef = useCallback((node: HTMLDivElement | null) => {
    if (node == null || typeof globalThis.ResizeObserver === 'undefined') return
    const measure = (): void => {
      const w = Math.round(node.clientWidth)
      if (w > 0) setWidth(Math.min(TICKER_BAR_MARQUEE_WIDTH, w))
    }
    const ro = new globalThis.ResizeObserver(measure)
    ro.observe(node)
    measure()
    return () => ro.disconnect()
  }, [])
  return { width, hostRef }
}

export const TickerBarEditorPreview = ({ draft }: { draft: TickerBarDraft }): JSX.Element => {
  const shown = usePreviewMessages(draft.topMessages, draft.bottomMessages)
  const { width, hostRef } = usePreviewWidth()
  const style = draft.style
  const fontKeys = useMemo(() => {
    const bars = style.showTopBar ? [style.top, style.bottom] : [style.bottom]
    return [...new Set(bars.flatMap((b) => [b.labelFontKey, b.textFontKey]))].sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
  }, [style.showTopBar, style.top, style.bottom])
  const fontSig = fontKeys.join('|')
  useEffect(() => {
    void Promise.all(fontKeys.map(async (key) => await ensureFontLoaded(key))).catch(() => {

    })
  }, [fontSig])
  const envelope = useMemo<TickerBarMarqueeInput>(() => ({
    style,
    topMessages: style.showTopBar ? shown.top : NO_MESSAGES,
    bottomMessages: shown.bottom
  }), [style, shown.top, shown.bottom])
  return (
    <div ref={hostRef} className='w-full overflow-hidden rounded-md' style={{ backgroundImage: 'linear-gradient(45deg, rgba(255,255,255,0.06) 25%, transparent 25%), linear-gradient(-45deg, rgba(255,255,255,0.06) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, rgba(255,255,255,0.06) 75%), linear-gradient(-45deg, transparent 75%, rgba(255,255,255,0.06) 75%)', backgroundSize: '16px 16px' }} data-testid='ticker-preview'>
      <TickerBarMarquee
        envelope={envelope}
        wrapWidth={width}
        restartKey={shown.restartKey}
        emptyBarHint={TICKER_BAR_COPY.emptyBarHint}
      />
    </div>
  )
}

export type TickerBarSaving = null | 'save' | 'restart'

export const TickerBarSaveBar = ({ isEdit, saving, blocked, ended, onSave, onShowNow, onCancel }: {
  isEdit: boolean
  saving: TickerBarSaving
  blocked: boolean
  ended: boolean
  onSave: () => void
  onShowNow: () => void
  onCancel: () => void
}): JSX.Element => {
  const busy = saving != null
  return (
    <div className='space-y-2'>
      <div className='flex items-center gap-2'>
        <button
          type='button'
          onClick={onSave}
          disabled={busy || blocked || ended}
          data-testid='ticker-save'
          className='inline-flex flex-1 items-center justify-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
        >
          {saving === 'save' && <LoadingSpinner />}
          {isEdit ? TICKER_BAR_MANAGER_COPY.saveButton : TICKER_BAR_MANAGER_COPY.createButton}
        </button>
        <button
          type='button'
          onClick={onCancel}
          disabled={busy}
          className='inline-flex flex-1 items-center justify-center rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:opacity-60'
        >
          {TICKER_BAR_MANAGER_COPY.cancel}
        </button>
      </div>
      {isEdit && (
        <div>
          <button
            type='button'
            onClick={onShowNow}
            disabled={busy || blocked || ended}
            data-testid='ticker-show-now'
            className='inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-brand/50 px-3 py-1.5 text-xs font-semibold text-brand hover:bg-brand/10 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {saving === 'restart' ? <LoadingSpinner /> : <ArrowPathIcon className='size-3.5' aria-hidden='true' />}
            {TICKER_BAR_MANAGER_COPY.showNowButton}
          </button>
          <p className='mt-1 text-[11px] text-dm-text-accent' data-testid='ticker-show-now-help'>{TICKER_BAR_COPY.showChangesHelp}</p>
        </div>
      )}
    </div>
  )
}

export interface TickerBarEditorProps {
  existing: TickerBar | null
  stream: { event: EventDetail } | null
  foreign: boolean
  overlayLink: AccountOverlayLinkState | null
  onDuplicate: (() => void) | null
  duplicating?: boolean
  savedInitially?: boolean
  onSaved: (row: TickerBar) => void
  onCancel: () => void
  backLabel: string
}

export const TickerBarEditor = ({
  existing,
  stream,
  foreign,
  overlayLink,
  onDuplicate,
  duplicating = false,
  savedInitially = false,
  onSaved,
  onCancel,
  backLabel
}: TickerBarEditorProps): JSX.Element => {
  const { auth0Token } = useAuth()
  const { staffUncapped } = useEntitlements()
  const isEdit = existing != null
  const streamEvent = stream == null ? null : stream.event
  const ended = streamEvent != null && streamEvent.status === 'ENDED'
  const [draft, setDraft] = useState<TickerBarDraft>(() => (existing == null ? newTickerBarDraft(staffUncapped) : tickerBarDraftOf(existing)))
  const [saving, setSaving] = useState<TickerBarSaving>(null)
  const savingRef = useRef(false)
  const [error, setError] = useState<string | null>(null)
  const [savedNote, setSavedNote] = useState(savedInitially)
  const firstDraftRef = useRef(true)
  useEffect(() => {
    if (firstDraftRef.current) {
      firstDraftRef.current = false
      return
    }
    setSavedNote(false)
  }, [draft])

  const problems = useMemo(() => tickerBarDraftProblems(draft), [draft])
  const nearBudget = useMemo(() => tickerBarNearBudget(draft), [draft])
  const summaryLines = tickerBarSummaryProblemLines(problems)
  const logoMode = foreign ? 'keepOrClear' : 'library'

  const save = async (restartNow: boolean): Promise<void> => {
    if (auth0Token == null || savingRef.current || ended) return
    if (tickerBarDraftProblems(draft).length > 0) return
    savingRef.current = true
    setSaving(restartNow ? 'restart' : 'save')
    setError(null)
    try {
      const { doc, variables } = tickerBarSaveRequestOf({
        draft,
        existing,
        eventId: streamEvent == null ? null : streamEvent.eventId,
        restartNow
      })
      const { tickerBar } = await graphqlRequest<{ tickerBar: TickerBar | null }>(doc, variables, auth0Token)
      if (tickerBar == null) {
        setError(TICKER_BAR_MANAGER_COPY.gone)
        return
      }
      setSavedNote(true)
      onSaved(tickerBar)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save the Message ticker')
    } finally {
      savingRef.current = false
      setSaving(null)
    }
  }

  const setBar = (which: 'top' | 'bottom') => (patch: Partial<TickerBar['style']['top']>): void =>
    setDraft((d) => ({ ...d, style: { ...d.style, [which]: { ...d.style[which], ...patch } } }))

  return (
    <div className='mt-2 grid grid-cols-1 gap-3 px-4 pb-4 lg:grid-cols-3' data-testid='ticker-editor'>
      <div className='lg:col-span-3'>
        <div className='sticky top-0 z-10 rounded-xl border border-white/5 bg-dm-bg-secondary p-4'>
          <h4 className='mb-2 text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>{TICKER_BAR_MANAGER_COPY.preview}</h4>
          <TickerBarEditorPreview draft={draft} />
        </div>
      </div>

      <div className='space-y-3 lg:col-span-2'>
        <SectionCard
          title={isEdit ? TICKER_BAR_MANAGER_COPY.editTitle : TICKER_BAR_MANAGER_COPY.createTitle}
          action={
            <button
              type='button'
              onClick={onCancel}
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary'
            >
              <ArrowLeftIcon className='size-3.5' aria-hidden='true' />
              {backLabel}
            </button>
          }
        >
          <label className='block'>
            <FieldLabel>Name</FieldLabel>
            <input
              value={draft.name}
              onChange={(e) => {
                const name = clipToUnits(e.target.value, MAX_TICKER_BAR_NAME)
                setDraft((d) => ({ ...d, name }))
              }}
              placeholder={TICKER_BAR_MANAGER_COPY.namePlaceholder}
              data-testid='ticker-name'
              className={tickerBarInputClass}
            />
            <p className='mt-1 text-[11px] text-dm-text-accent'>{TICKER_BAR_MANAGER_COPY.nameHelp}</p>
          </label>
        </SectionCard>
        <TickerBarStyleCard
          which='top'
          bar={draft.style.top}
          messages={draft.topMessages}
          showTopBar={draft.style.showTopBar}
          onShowTopBarChange={(on) => setDraft((d) => ({ ...d, style: { ...d.style, showTopBar: on } }))}
          onBarChange={setBar('top')}
          onMessagesChange={(next) => setDraft((d) => ({ ...d, topMessages: next }))}
          problems={problems.filter((p) => p.place === 'top')}
          logoMode={logoMode}
          savedLogoUrl={existing == null ? null : existing.style.top.logoUrl}
        />
        <TickerBarStyleCard
          which='bottom'
          bar={draft.style.bottom}
          messages={draft.bottomMessages}
          onBarChange={setBar('bottom')}
          onMessagesChange={(next) => setDraft((d) => ({ ...d, bottomMessages: next }))}
          problems={problems.filter((p) => p.place === 'bottom')}
          logoMode={logoMode}
          savedLogoUrl={existing == null ? null : existing.style.bottom.logoUrl}
        />
        <TickerBarSharedCard
          style={draft.style}
          onChange={(patch) => setDraft((d) => ({ ...d, style: { ...d.style, ...patch } }))}
        />
      </div>

      <div className='lg:col-span-1'>
        <div className='sticky top-40 space-y-3'>
          <TickerBarSaveBar
            isEdit={isEdit}
            saving={saving}
            blocked={problems.length > 0}
            ended={ended}
            onSave={() => { void save(false) }}
            onShowNow={() => { void save(true) }}
            onCancel={onCancel}
          />
          {summaryLines.length > 0 && (
            <div className='rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200' data-testid='ticker-problems'>
              <p className='font-semibold'>{TICKER_BAR_MANAGER_COPY.problemsLead}</p>
              <ul className='mt-1 list-disc space-y-0.5 pl-4'>
                {summaryLines.map((line) => <li key={line}>{line}</li>)}
              </ul>
            </div>
          )}
          {nearBudget && (
            <p className='text-[11px] text-amber-300' data-testid='ticker-near-budget'>{TICKER_BAR_COPY.nearBudget}</p>
          )}
          {error != null && <ErrorInline onDismiss={() => setError(null)}>{error}</ErrorInline>}
          {savedNote && <SavedNote testId='ticker-saved' />}
          {ended && (
            <p className='text-[11px] text-dm-text-accent' data-testid='stream-ended-note'>{STREAM_ENDED_NOTE}</p>
          )}

          {streamEvent != null && (
            <p className='rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2 text-[11px] leading-relaxed text-dm-text-secondary' data-testid='stream-ticker-side-note'>
              {STREAM_TICKER_BAR_COPY.sideNote}
            </p>
          )}

          {streamEvent == null && foreign && (
            <p className='rounded-md border border-white/5 bg-dm-bg-accent/30 px-3 py-2 text-[11px] leading-relaxed text-dm-text-secondary' data-testid='ticker-foreign-note'>
              {TICKER_BAR_MANAGER_COPY.foreignNote}
            </p>
          )}

          {streamEvent == null && !foreign && isEdit && overlayLink != null && (
            <>
              <h4 className='text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
                {TICKER_BAR_MANAGER_COPY.linkHeading}
              </h4>
              <OverlayLinkBlock link={overlayLink} label='Message ticker' />
            </>
          )}
          {streamEvent == null && !isEdit && (
            <p className='text-[11px] text-dm-text-accent'>{TICKER_BAR_MANAGER_COPY.linkAfterSave}</p>
          )}
          {streamEvent == null && !foreign && isEdit && onDuplicate != null && (
            <button
              type='button'
              onClick={onDuplicate}
              disabled={duplicating || saving != null}
              data-testid='ticker-duplicate'
              className='inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
            >
              {duplicating ? <LoadingSpinner /> : <DocumentDuplicateIcon className='size-3.5' aria-hidden='true' />}
              {TICKER_BAR_MANAGER_COPY.duplicate}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

const TickerBarListRow = ({
  row,
  onEdit,
  onDuplicate,
  onDelete,
  busyAction,
  atCap
}: {
  row: TickerBarSummary
  onEdit: () => void
  onDuplicate: () => void
  onDelete: () => void
  busyAction: null | 'delete' | 'duplicate'
  atCap: boolean
}): JSX.Element => {
  const busy = busyAction != null
  const overlayLink = useAccountOverlayLink('tickerbar', row.tickerBarId)
  return (
    <li className='rounded-lg border border-white/5 bg-dm-bg-accent/30 px-4 py-3' data-testid='ticker-row'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='flex min-w-0 items-center gap-3'>
          <span className='shrink-0 rounded-full bg-dm-bg-accent/50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent ring-1 ring-inset ring-white/10'>
            Message ticker
          </span>
          <div className='flex min-w-0 flex-col leading-tight'>
            <span className='truncate text-sm font-medium text-dm-text-primary'>{row.name}</span>
            <span className='truncate text-[11px] text-dm-text-accent'>{updatedLabelOf(row.updatedAt)}</span>
          </div>
        </div>
        <div className='flex shrink-0 items-center gap-1.5'>
          <OverlayLinkRowActions link={overlayLink} label={`${row.name} Message ticker`} />
          <button
            type='button'
            onClick={onEdit}
            title='Edit'
            disabled={busy}
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            <PencilSquareIcon className='size-3.5' aria-hidden='true' />
            Edit
          </button>
          <button
            type='button'
            onClick={onDuplicate}
            title={atCap ? "Your account's Message ticker limit is reached" : 'Duplicate'}
            disabled={busy || atCap}
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busyAction === 'duplicate' ? <LoadingSpinner /> : <DocumentDuplicateIcon className='size-3.5' aria-hidden='true' />}
            Duplicate
          </button>
          <button
            type='button'
            onClick={onDelete}
            disabled={busy}
            title='Delete'
            className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busyAction === 'delete' ? <LoadingSpinner /> : <TrashIcon className='size-3.5' aria-hidden='true' />}
            Delete
          </button>
        </div>
      </div>
    </li>
  )
}

const useQuickTickerBar = (tickerBarId: string | null, known: TickerBar | null): { row: TickerBar | null | 'gone', error: string | null, retry: () => void } => {
  const { auth0Token } = useAuth()
  const [loaded, setLoaded] = useState<{ id: string, row: TickerBar | null } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)
  useEffect(() => {
    if (tickerBarId == null || auth0Token == null) return
    if (known != null && known.tickerBarId === tickerBarId) return
    let cancelled = false
    setError(null)
    graphqlRequest<{ tickerBar: TickerBar | null }>(getTickerBarQuery, { tickerBarId }, auth0Token)
      .then(({ tickerBar }) => {
        if (!cancelled) setLoaded({ id: tickerBarId, row: tickerBar })
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load the Message ticker')
      })
    return () => { cancelled = true }
  }, [tickerBarId, auth0Token, known, attempt])
  const retry = useCallback(() => setAttempt((a) => a + 1), [])
  if (tickerBarId == null) return { row: null, error: null, retry }
  if (known != null && known.tickerBarId === tickerBarId) return { row: known, error: null, retry }
  if (loaded != null && loaded.id === tickerBarId) return { row: loaded.row ?? 'gone', error, retry }
  return { row: null, error, retry }
}

const QuickTickerBarEditorView = ({ view, basePath, backLabel }: {
  view: 'create' | { edit: string }
  basePath: string
  backLabel: string
}): JSX.Element => {
  const { auth0Token, streamToolsUser } = useAuth()
  const { account } = useEntitlements()
  const navigate = useNavigate()
  const callerAccountId = account?.accountId ?? streamToolsUser?.userId ?? null
  const [known, setKnown] = useState<TickerBar | null>(null)
  const [createdId, setCreatedId] = useState<string | null>(null)
  const [duplicating, setDuplicating] = useState(false)
  const [duplicateError, setDuplicateError] = useState<string | null>(null)
  const editId = typeof view === 'object' ? view.edit : null
  const { row, error, retry } = useQuickTickerBar(editId, known)
  const existing = row === 'gone' ? null : row
  const foreign = isForeignTickerBar(existing, callerAccountId)
  const editLink = useAccountOverlayLink('tickerbar', existing != null && !foreign ? existing.tickerBarId : null)
  const goList = (): void => { void navigate(basePath) }

  const handleDuplicate = async (source: TickerBar): Promise<void> => {
    if (auth0Token == null || duplicating) return
    setDuplicating(true)
    setDuplicateError(null)
    try {
      const { tickerBar } = await graphqlRequest<{ tickerBar: TickerBar | null }>(duplicateTickerBarMutation, { tickerBarId: source.tickerBarId }, auth0Token)
      if (tickerBar == null) {
        setDuplicateError('Failed to duplicate the Message ticker')
        return
      }
      setKnown(tickerBar)
      void navigate(`${basePath}/${tickerBar.tickerBarId}`)
    } catch (err) {
      setDuplicateError(err instanceof Error ? err.message : 'Failed to duplicate the Message ticker')
    } finally {
      setDuplicating(false)
    }
  }

  if (view === 'create') {
    return (
      <TickerBarEditor
        key='new'
        existing={null}
        stream={null}
        foreign={false}
        overlayLink={null}
        onDuplicate={null}
        onSaved={(saved) => {
          setKnown(saved)
          setCreatedId(saved.tickerBarId)
          void navigate(`${basePath}/${saved.tickerBarId}`, { replace: true })
        }}
        onCancel={goList}
        backLabel={backLabel}
      />
    )
  }

  if (row == null) {
    if (error != null) {
      return (
        <div className='mt-4 px-4 pb-4'>
          <ErrorInline>{error}</ErrorInline>
          <button onClick={retry} className='mt-3 rounded-md border border-white/10 px-3 py-1.5 text-xs text-dm-text-secondary hover:text-dm-text-primary'>
            Retry
          </button>
        </div>
      )
    }
    return (
      <div className='mt-4 flex items-center justify-center px-4 py-10 pb-4'>
        <LoadingSpinner />
      </div>
    )
  }
  if (row === 'gone') {
    return (
      <div className='mt-4 px-4 pb-4'>
        <ErrorInline>{TICKER_BAR_MANAGER_COPY.gone}</ErrorInline>
        <button onClick={goList} className='mt-3 rounded-md border border-white/10 px-3 py-1.5 text-xs text-dm-text-secondary hover:text-dm-text-primary'>
          {backLabel}
        </button>
      </div>
    )
  }
  return (
    <>
      {duplicateError != null && (
        <div className='mt-2 px-4'>
          <ErrorInline onDismiss={() => setDuplicateError(null)}>{duplicateError}</ErrorInline>
        </div>
      )}
      <TickerBarEditor
        key={row.tickerBarId}
        existing={row}
        stream={null}
        foreign={foreign}
        overlayLink={foreign ? null : editLink}
        onDuplicate={foreign ? null : () => { void handleDuplicate(row) }}
        duplicating={duplicating}
        savedInitially={createdId === row.tickerBarId}
        onSaved={setKnown}
        onCancel={goList}
        backLabel={backLabel}
      />
    </>
  )
}

const QuickTickerBarList = ({ basePath }: { basePath: string }): JSX.Element => {
  const { auth0Token } = useAuth()
  const navigate = useNavigate()
  const [rows, setRows] = useState<TickerBarSummary[] | null>(null)
  const [listBusy, setListBusy] = useState(false)
  const [listError, setListError] = useState<string | null>(null)
  const [rowBusy, setRowBusy] = useState<{ id: string, action: 'delete' | 'duplicate' } | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  const load = useCallback(async (): Promise<void> => {
    if (auth0Token == null) return
    setListBusy(true)
    setListError(null)
    try {
      const { tickerBars } = await graphqlRequest<{ tickerBars: TickerBarSummary[] }>(listMyTickerBarsQuery, {}, auth0Token)
      setRows(tickerBars)
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to load Message tickers')
    } finally {
      setListBusy(false)
    }
  }, [auth0Token])

  useEffect(() => {
    void load()
  }, [load])

  const { enforcedLimitFor, usage } = useEntitlements()
  const cap = enforcedLimitFor('tickerBarsPerAccount')
  const held = Math.max(usage.tickerBars ?? 0, rows == null ? 0 : rows.length)
  const atCap = cap != null && held >= cap

  const goCreate = (): void => { void navigate(`${basePath}/new`) }
  const goEdit = (id: string): void => { void navigate(`${basePath}/${id}`) }

  const handleDelete = async (row: TickerBarSummary): Promise<void> => {
    if (auth0Token == null || rowBusy != null) return
    if (!globalThis.confirm(TICKER_BAR_MANAGER_COPY.deleteConfirm(row.name))) return
    setRowBusy({ id: row.tickerBarId, action: 'delete' })
    setRowError(null)
    try {
      const { result } = await graphqlRequest<{ result: DeletionResult }>(deleteTickerBarMutation, { tickerBarId: row.tickerBarId }, auth0Token)
      if (result.deleted) setRows((prev) => (prev == null ? prev : prev.filter((r) => r.tickerBarId !== row.tickerBarId)))
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to delete the Message ticker')
    } finally {
      setRowBusy(null)
    }
  }

  const handleDuplicate = async (row: TickerBarSummary): Promise<void> => {
    if (auth0Token == null || rowBusy != null) return
    setRowBusy({ id: row.tickerBarId, action: 'duplicate' })
    setRowError(null)
    try {
      const { tickerBar } = await graphqlRequest<{ tickerBar: TickerBar | null }>(duplicateTickerBarMutation, { tickerBarId: row.tickerBarId }, auth0Token)
      if (tickerBar == null) {
        setRowError('Failed to duplicate the Message ticker')
        return
      }
      setRows((prev) => [...(prev ?? []), tickerBarSummaryOf(tickerBar)])
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to duplicate the Message ticker')
    } finally {
      setRowBusy(null)
    }
  }

  let listContent: JSX.Element
  if (rows == null) {
    listContent = (
      <div className='flex items-center justify-center rounded-lg border border-dashed border-white/10 px-4 py-10'>
        <LoadingSpinner />
      </div>
    )
  } else if (rows.length === 0) {
    listContent = (
      <div className='flex flex-col items-center gap-3 rounded-lg border border-dashed border-white/10 px-4 py-10 text-center text-sm text-dm-text-secondary' data-testid='ticker-empty'>
        <MegaphoneIcon className='size-8 text-dm-text-accent' aria-hidden='true' />
        <p>{TICKER_BAR_MANAGER_COPY.empty}</p>
        <button
          type='button'
          onClick={goCreate}
          disabled={atCap}
          className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
        >
          <PlusIcon className='size-3.5' aria-hidden='true' />
          {TICKER_BAR_MANAGER_COPY.createFirst}
        </button>
      </div>
    )
  } else {
    listContent = (
      <ul className='space-y-2' data-testid='ticker-list'>
        {sortByName(rows, (r) => r.name, (r) => r.tickerBarId).map((row) => (
          <TickerBarListRow
            key={row.tickerBarId}
            row={row}
            onEdit={() => goEdit(row.tickerBarId)}
            onDuplicate={() => { void handleDuplicate(row) }}
            onDelete={() => { void handleDelete(row) }}
            busyAction={rowBusy?.id === row.tickerBarId ? rowBusy.action : null}
            atCap={atCap}
          />
        ))}
      </ul>
    )
  }

  return (
    <div className='mt-2 flex flex-col gap-1.5 px-4 pb-4'>
      <SectionCard
        title={TICKER_BAR_MANAGER_COPY.heading}
        action={
          <div className='flex items-center gap-2'>
            <button
              type='button'
              onClick={() => { void load() }}
              disabled={listBusy}
              title='Refresh'
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1.5 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
            >
              {listBusy ? <LoadingSpinner /> : <ArrowPathIcon className='size-3.5' aria-hidden='true' />}
              Refresh
            </button>
            <button
              type='button'
              data-testid='add-ticker-button'
              onClick={goCreate}
              disabled={atCap}
              className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
            >
              <PlusIcon className='size-3.5' aria-hidden='true' />
              {TICKER_BAR_MANAGER_COPY.newButton}
            </button>
          </div>
        }
      >
        {listError != null && (
          <div className='mb-3'>
            <ErrorInline onDismiss={() => setListError(null)}>{listError}</ErrorInline>
          </div>
        )}
        {rowError != null && (
          <div className='mb-3'>
            <ErrorInline onDismiss={() => setRowError(null)}>{rowError}</ErrorInline>
          </div>
        )}
        {atCap && cap != null && (
          <div className='mb-3 rounded-md border border-white/10 bg-dm-bg-accent/30 px-3 py-2 text-[11px] text-dm-text-secondary'>
            {TICKER_BAR_MANAGER_COPY.capReached(cap)}
          </div>
        )}
        {listContent}
        <div className='mt-4 space-y-1 text-[11px] text-dm-text-accent'>
          <p>{TICKER_BAR_MANAGER_COPY.listFooter}</p>
          <p>{OVERLAY_LINK_NOTE}</p>
        </div>
      </SectionCard>
    </div>
  )
}

const StreamTickerBarRow = ({ row, onEdit, onDelete, deleting, busy }: {
  row: TickerBar
  onEdit: () => void
  onDelete: () => void
  deleting: boolean
  busy: boolean
}): JSX.Element => (
  <li className='rounded-lg border border-white/5 bg-dm-bg-accent/30 px-4 py-3' data-testid='stream-ticker-row'>
    <div className='flex flex-wrap items-center justify-between gap-3'>
      <div className='flex min-w-0 items-center gap-3'>
        <span className='shrink-0 rounded-full bg-dm-bg-accent/50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent ring-1 ring-inset ring-white/10'>
          Message ticker
        </span>
        <div className='flex min-w-0 flex-col leading-tight'>
          <span className='truncate text-sm font-medium text-dm-text-primary'>{row.name}</span>
          <span className='truncate text-[11px] text-dm-text-accent'>{updatedLabelOf(row.updatedAt)}</span>
        </div>
      </div>
      <div className='flex shrink-0 items-center gap-1.5'>
        <button
          type='button'
          onClick={onEdit}
          title='Edit'
          disabled={busy}
          className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
        >
          <PencilSquareIcon className='size-3.5' aria-hidden='true' />
          Edit
        </button>
        <button
          type='button'
          onClick={onDelete}
          disabled={busy}
          title='Delete'
          className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-2.5 py-1 text-xs text-dm-text-secondary hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-60'
        >
          {deleting ? <LoadingSpinner /> : <TrashIcon className='size-3.5' aria-hidden='true' />}
          Delete
        </button>
      </div>
    </div>
  </li>
)

export const StreamTickerBarManager = ({ view, basePath, backLabel, scope }: {
  view: QuickOverlayManagerView
  basePath: string
  backLabel: string
  scope: StreamScope
}): JSX.Element => {
  const { auth0Token } = useAuth()
  const navigate = useNavigate()
  const { event, refetchEvent, setEvent } = scope
  const ended = event.status === 'ENDED'
  const rows = useMemo(() => streamTickerBarRowsOf(event), [event])
  const full = rows.length >= MAX_TICKER_BARS_PER_STREAM
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)
  const [copyOpen, setCopyOpen] = useState(false)
  const [createdId, setCreatedId] = useState<string | null>(null)

  const goList = (): void => { void navigate(basePath) }
  const goCreate = (): void => { void navigate(`${basePath}/new`) }
  const goEdit = (id: string): void => { void navigate(`${basePath}/${id}`) }

  const refresh = (fold: (prev: EventDetail) => EventDetail): void => {
    void refetchEvent()
      .then(() => { reapplyStreamWrite(setEvent, fold) })
      .catch(() => {

      })
  }
  const upsert = (row: TickerBar): void => {
    const fold = (prev: EventDetail): EventDetail => withStreamTickerBarRow(prev, row)
    reapplyStreamWrite(setEvent, fold)
    refresh(fold)
  }

  const handleDelete = async (row: TickerBar): Promise<void> => {
    if (auth0Token == null || deletingId != null) return
    if (!globalThis.confirm(STREAM_TICKER_BAR_COPY.deleteConfirm(row.name))) return
    setDeletingId(row.tickerBarId)
    setRowError(null)
    try {
      await graphqlRequest<{ result: DeletionResult }>(
        deleteEventTickerBarMutation, { eventId: event.eventId, tickerBarId: row.tickerBarId }, auth0Token
      )
      const fold = (prev: EventDetail): EventDetail => withoutStreamTickerBarRow(prev, row.tickerBarId)
      reapplyStreamWrite(setEvent, fold)
      refresh(fold)
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Failed to delete the Message ticker')
    } finally {
      setDeletingId(null)
    }
  }

  const streamForm = { event }

  if (view === 'create') {
    return (
      <TickerBarEditor
        key='new'
        existing={null}
        stream={streamForm}
        foreign={false}
        overlayLink={null}
        onDuplicate={null}
        onSaved={(row) => {
          upsert(row)
          setCreatedId(row.tickerBarId)
          void navigate(`${basePath}/${row.tickerBarId}`, { replace: true })
        }}
        onCancel={goList}
        backLabel={backLabel}
      />
    )
  }

  if (typeof view === 'object') {
    const editing = (event.tickerBars ?? []).find((t) => t.tickerBarId === view.edit) ?? null
    if (editing == null) {
      return (
        <div className='mt-4 px-4 pb-4'>
          <ErrorInline>{TICKER_BAR_MANAGER_COPY.gone}</ErrorInline>
          <button onClick={goList} className='mt-3 rounded-md border border-white/10 px-3 py-1.5 text-xs text-dm-text-secondary hover:text-dm-text-primary'>
            {backLabel}
          </button>
        </div>
      )
    }
    return (
      <TickerBarEditor
        key={editing.tickerBarId}
        existing={editing}
        stream={streamForm}
        foreign={false}
        overlayLink={null}
        onDuplicate={null}
        savedInitially={createdId === editing.tickerBarId}
        onSaved={upsert}
        onCancel={goList}
        backLabel={backLabel}
      />
    )
  }

  let listContent: JSX.Element
  if (rows.length === 0) {
    listContent = (
      <div className='flex flex-col items-center gap-3 rounded-lg border border-dashed border-white/10 px-4 py-10 text-center text-sm text-dm-text-secondary' data-testid='stream-ticker-empty'>
        <MegaphoneIcon className='size-8 text-dm-text-accent' aria-hidden='true' />
        <p>{STREAM_TICKER_BAR_COPY.empty}</p>
      </div>
    )
  } else {
    listContent = (
      <ul className='space-y-2' data-testid='stream-ticker-list'>
        {rows.map((row) => (
          <StreamTickerBarRow
            key={row.tickerBarId}
            row={row}
            onEdit={() => goEdit(row.tickerBarId)}
            onDelete={() => { void handleDelete(row) }}
            deleting={deletingId === row.tickerBarId}
            busy={deletingId != null}
          />
        ))}
      </ul>
    )
  }

  return (
    <div className='mt-2 flex flex-col gap-1.5 px-4 pb-4'>
      <section className='rounded-xl border border-white/5 bg-dm-bg-secondary p-5' data-testid='stream-tickers'>
        <div className='mb-4 flex flex-wrap items-center justify-between gap-3'>
          <h3 className='text-base font-semibold text-dm-text-primary'>{STREAM_TICKER_BAR_COPY.heading}</h3>
          <div className='flex flex-wrap items-center gap-2'>
            <button
              type='button'
              onClick={() => setCopyOpen(true)}
              disabled={ended || full}
              data-testid='stream-ticker-copy-button'
              className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
            >
              <DocumentDuplicateIcon className='size-3.5' aria-hidden='true' />
              {STREAM_TICKER_BAR_COPY.copyButton}
            </button>
            <button
              type='button'
              data-testid='stream-ticker-new-button'
              onClick={goCreate}
              disabled={ended || full}
              className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
            >
              <PlusIcon className='size-3.5' aria-hidden='true' />
              {STREAM_TICKER_BAR_COPY.newButton}
            </button>
          </div>
        </div>
        {ended && (
          <p className='mb-3 text-[11px] text-dm-text-accent' data-testid='stream-ended-note'>{STREAM_ENDED_NOTE}</p>
        )}
        {!ended && full && (
          <p className='mb-3 text-[11px] text-dm-text-accent' data-testid='stream-ticker-full-note'>{STREAM_TICKER_BAR_COPY.atMost}.</p>
        )}
        {rowError != null && (
          <div className='mb-3'>
            <ErrorInline onDismiss={() => setRowError(null)}>{rowError}</ErrorInline>
          </div>
        )}
        {listContent}
        <p className='mt-4 text-[11px] text-dm-text-accent' data-testid='stream-ticker-note'>
          {STREAM_TICKER_BAR_COPY.note}
        </p>
      </section>
      {copyOpen && (
        <CopyIntoStreamDialog
          kind='tickerbar'
          event={event}
          onCopied={(row) => { upsert(row as TickerBar) }}
          onClose={() => setCopyOpen(false)}
        />
      )}
    </div>
  )
}

export const TickerBarManager = ({ view, basePath, backLabel, scope }: {
  view: QuickOverlayManagerView
  basePath: string
  backLabel: string
  scope?: StreamScope
}): JSX.Element => {
  if (scope != null) {
    return <StreamTickerBarManager view={view} basePath={basePath} backLabel={backLabel} scope={scope} />
  }
  if (view === 'list') return <QuickTickerBarList basePath={basePath} />
  return <QuickTickerBarEditorView view={view} basePath={basePath} backLabel={backLabel} />
}
